import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Search, Phone, MessageSquare, ChevronRight, Plus, X, Loader2, Save, Trash2, FileText } from 'lucide-react';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(p || 0);
const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const Field = ({ label, value, onChange, type = 'text', testId }) => (
  <div>
    <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
           className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30"
           data-testid={testId} />
  </div>
);

function ContactForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.contact_id;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: initial?.name || '', phone: initial?.phone || '', email: initial?.email || '',
    company: initial?.company || '', notes: initial?.contact_notes || initial?.notes || '',
  });
  const setF = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/api/admin/contacts/${initial.contact_id}`, form);
      else await api.post('/api/admin/contacts', form);
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('Delete this contact?')) return;
    setBusy(true);
    try {
      await api.delete(`/api/admin/contacts/${initial.contact_id}`);
      toast.success('Contact deleted');
      onSaved();
    } catch (e) { toast.error('Delete failed'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="contact-form-modal">
      <motion.div
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="w-full max-w-lg bg-[#0C0C0C] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white text-xl font-black tracking-tighter" style={FONT_H}>{isEdit ? 'Edit Contact' : 'New Contact'}</h3>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-300 active:scale-95 transition-transform" data-testid="contact-form-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-4">
          <Field label="Name" value={form.name} onChange={setF('name')} testId="contact-name" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone" value={form.phone} onChange={setF('phone')} type="tel" testId="contact-phone" />
            <Field label="Email" value={form.email} onChange={setF('email')} type="email" testId="contact-email" />
          </div>
          <Field label="Company / Where you met" value={form.company} onChange={setF('company')} testId="contact-company" />
          <div>
            <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>Notes</span>
            <textarea value={form.notes} onChange={(e) => setF('notes')(e.target.value)} rows={3}
                      className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30 resize-none" data-testid="contact-notes" />
          </div>
          <div className="flex items-center gap-2.5 pt-1">
            <button onClick={save} disabled={busy}
                    className="flex items-center gap-2 px-7 py-3 rounded-full bg-white text-black text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid="contact-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            {isEdit && (
              <button onClick={remove} disabled={busy}
                      className="flex items-center gap-2 px-5 py-3 rounded-full bg-red-500/10 text-red-400 text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid="contact-delete">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export const ContactsView = ({ onOpenLead }) => {
  const [contacts, setContacts] = useState(null);
  const [q, setQ] = useState('');
  const [formFor, setFormFor] = useState(null); // {} = new, contact obj = edit

  const load = useCallback(async (query) => {
    try {
      const data = await api.get(`/api/admin/contacts${query ? `?q=${encodeURIComponent(query)}` : ''}`);
      setContacts(data.contacts);
    } catch (err) {
      console.error('Failed to load contacts', err);
      setContacts([]);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const openContact = (c) => {
    if (c.contact_id) setFormFor(c);
    else if (c.last_proposal_id) onOpenLead(c.last_proposal_id);
  };

  if (contacts === null) return <p className="text-zinc-600 text-sm py-16 text-center" style={FONT_B}>Loading…</p>;

  return (
    <div data-testid="contacts-view">
      <div className="flex items-center gap-2.5 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-600 absolute left-4 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email…"
                 className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-white/30"
                 style={FONT_B} data-testid="contacts-search" />
        </div>
        <button onClick={() => setFormFor({})}
                className="flex items-center gap-1.5 px-5 py-3 rounded-full bg-white text-black text-xs font-bold active:scale-95 flex-shrink-0 transition-transform"
                style={FONT_B} data-testid="add-contact-btn">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>

      <p className="text-zinc-600 text-xs mb-4" style={FONT_B}>{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>

      {contacts.length === 0 && <p className="text-zinc-600 text-sm text-center py-12" style={FONT_B}>No contacts found</p>}

      <div className="space-y-2.5">
        {contacts.map((c, i) => (
          <div key={i} className="flex items-center gap-4 bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 cursor-pointer active:scale-[0.99] transition-transform"
               onClick={() => openContact(c)} data-testid={`contact-${i}`}>
            <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${c.contact_id ? 'bg-fuchsia-500/10' : 'bg-white/5'}`}>
              <span className={`text-sm font-black ${c.contact_id ? 'text-fuchsia-300' : 'text-zinc-300'}`} style={FONT_H}>
                {(c.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-[15px] font-bold tracking-tight truncate" style={FONT_H}>{c.name || c.phone || c.email}</p>
              <p className="text-zinc-600 text-[11px] truncate" style={FONT_B}>
                {c.leads > 0 ? (
                  <>
                    {c.leads} booking{c.leads !== 1 ? 's' : ''} · <span className="text-emerald-400 font-bold tabular-nums">{fmt(c.total_paid)} paid</span>
                    {c.total_value > c.total_paid ? <span className="text-zinc-500"> of {fmt(c.total_value)}</span> : ''}
                  </>
                ) : (
                  <span className="text-fuchsia-400/70 font-bold">{c.company || 'Added contact'}</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {c.phone && (
                <>
                  <a href={`tel:${c.phone}`} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 active:scale-95 transition-transform" aria-label="Call">
                    <Phone className="w-4 h-4" />
                  </a>
                  <a href={`sms:${c.phone}`} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 active:scale-95 transition-transform" aria-label="Text">
                    <MessageSquare className="w-4 h-4" />
                  </a>
                </>
              )}
              {c.contact_id && c.last_proposal_id && (
                <button onClick={() => onOpenLead(c.last_proposal_id)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 active:scale-95 transition-transform" aria-label="View lead" data-testid={`contact-lead-${i}`}>
                  <FileText className="w-4 h-4" />
                </button>
              )}
              <ChevronRight className="w-4 h-4 text-zinc-700" />
            </div>
          </div>
        ))}
      </div>

      {formFor && (
        <ContactForm
          initial={formFor.contact_id ? formFor : null}
          onClose={() => setFormFor(null)}
          onSaved={() => { setFormFor(null); load(q); }}
        />
      )}
    </div>
  );
};
